import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import {
  patientReportTextErrorMessage,
  type PatientReportText,
  type PatientReportTextType,
} from '@/hooks/usePatientReportTexts';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export function ReportTextLibrary({
  types,
  texts,
  loading,
  error,
  busy,
  disabled,
  usedTypeIds,
  canManageType,
  onCreate,
  onRename,
  onDeleteType,
  onSaveText,
  onDeleteText,
  onInsert,
}: {
  types: readonly PatientReportTextType[];
  texts: readonly PatientReportText[];
  loading: boolean;
  error: boolean;
  busy: boolean;
  disabled?: boolean;
  usedTypeIds: ReadonlySet<string>;
  canManageType: (type: PatientReportTextType) => boolean;
  onCreate: (input: { name: string; body: string }) => Promise<void>;
  onRename: (input: { id: string; name: string }) => Promise<void>;
  onDeleteType: (id: string) => Promise<void>;
  onSaveText: (input: { typeId: string; body: string }) => Promise<void>;
  onDeleteText: (id: string) => Promise<void>;
  onInsert: (type: PatientReportTextType) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const labels = {
    duplicate: t('patientReports.texts_duplicate'),
    cpr: t('patientReports.texts_cpr_blocked'),
  };

  const create = async () => {
    if (!name.trim()) {
      setFormError(t('patientReports.texts_name_required'));
      return;
    }
    setFormError(null);
    try {
      await onCreate({ name, body });
      setName('');
      setBody('');
    } catch (caught) {
      setFormError(
        patientReportTextErrorMessage(caught, t('patientReports.texts_save_error'), labels),
      );
    }
  };

  return (
    <div data-testid="report-text-library" className="flex flex-col gap-s3">
      {loading ? (
        <p className={cn(TYPE.small, 'text-ds-muted')}>{t('patientReports.loading')}</p>
      ) : null}
      {error ? (
        <p className={cn(TYPE.small, 'text-ds-red-ink')}>{t('patientReports.texts_error')}</p>
      ) : null}
      <div className="flex flex-col gap-s2 rounded-ctl border border-ds-line bg-ds-sunk p-s3">
        <Input
          id="report-text-type-name"
          label={t('patientReports.texts_type_name')}
          value={name}
          maxLength={80}
          disabled={disabled || busy}
          placeholder={t('patientReports.texts_type_placeholder')}
          onChange={(event) => setName(event.target.value)}
        />
        <Textarea
          id="report-text-type-body"
          label={t('patientReports.texts_body')}
          hint={t('patientReports.texts_body_hint')}
          value={body}
          maxLength={20000}
          disabled={disabled || busy}
          className="min-h-24"
          onChange={(event) => setBody(event.target.value)}
        />
        {formError ? <p className={cn(TYPE.small, 'text-ds-red-ink')}>{formError}</p> : null}
        <Button type="button" disabled={disabled} loading={busy} onClick={() => void create()}>
          {t('patientReports.texts_create')}
        </Button>
      </div>
      {types.length === 0 && !loading ? (
        <p className={cn(TYPE.small, 'text-ds-muted')}>{t('patientReports.texts_empty')}</p>
      ) : (
        types.map((type) => (
          <TextTypeRow
            key={`${type.id}:${texts.find((text) => text.typeId === type.id)?.body ?? ''}`}
            type={type}
            text={texts.find((text) => text.typeId === type.id) ?? null}
            inserted={usedTypeIds.has(type.id)}
            canManage={canManageType(type)}
            busy={busy}
            disabled={disabled}
            onRename={onRename}
            onDeleteType={onDeleteType}
            onSaveText={onSaveText}
            onDeleteText={onDeleteText}
            onInsert={() => onInsert(type)}
          />
        ))
      )}
    </div>
  );
}

function TextTypeRow({
  type,
  text,
  inserted,
  canManage,
  busy,
  disabled,
  onRename,
  onDeleteType,
  onSaveText,
  onDeleteText,
  onInsert,
}: {
  type: PatientReportTextType;
  text: PatientReportText | null;
  inserted: boolean;
  canManage: boolean;
  busy: boolean;
  disabled?: boolean;
  onRename: (input: { id: string; name: string }) => Promise<void>;
  onDeleteType: (id: string) => Promise<void>;
  onSaveText: (input: { typeId: string; body: string }) => Promise<void>;
  onDeleteText: (id: string) => Promise<void>;
  onInsert: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(type.name);
  const [body, setBody] = useState(text?.body ?? '');
  const [rowError, setRowError] = useState<string | null>(null);
  const labels = {
    duplicate: t('patientReports.texts_duplicate'),
    cpr: t('patientReports.texts_cpr_blocked'),
  };

  const run = async (action: () => Promise<void>) => {
    setRowError(null);
    try {
      await action();
    } catch (caught) {
      setRowError(
        patientReportTextErrorMessage(caught, t('patientReports.texts_save_error'), labels),
      );
    }
  };

  return (
    <div
      data-testid={`report-text-type-${type.id}`}
      className="flex flex-col gap-s2 border-t border-ds-line pt-s3"
    >
      {canManage ? (
        <div className="flex flex-col gap-s2">
          <Input
            label={t('patientReports.texts_type_name')}
            value={name}
            maxLength={80}
            disabled={disabled || busy}
            onChange={(event) => setName(event.target.value)}
          />
          <Button
            type="button"
            variant="secondary"
            disabled={disabled || busy || name.trim() === type.name}
            onClick={() => void run(() => onRename({ id: type.id, name }))}
          >
            {t('patientReports.texts_rename')}
          </Button>
        </div>
      ) : (
        <p className={cn(TYPE.body, 'font-semibold text-ds-text')}>{type.name}</p>
      )}
      <Textarea
        label={t('patientReports.texts_body')}
        hint={t('patientReports.texts_body_hint')}
        value={body}
        maxLength={20000}
        disabled={disabled || busy}
        className="min-h-24"
        onChange={(event) => setBody(event.target.value)}
      />
      {rowError ? <p className={cn(TYPE.small, 'text-ds-red-ink')}>{rowError}</p> : null}
      <div className="flex flex-wrap gap-s2">
        <Button
          type="button"
          variant="secondary"
          disabled={disabled || busy}
          onClick={() => void run(() => onSaveText({ typeId: type.id, body }))}
        >
          {t('patientReports.texts_save')}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={disabled || inserted}
          data-testid={`report-text-insert-${type.id}`}
          onClick={onInsert}
        >
          {inserted ? t('patientReports.texts_inserted_already') : t('patientReports.texts_insert')}
        </Button>
        {text ? (
          <Button
            type="button"
            variant="tertiary"
            disabled={disabled || busy}
            onClick={() => {
              if (!window.confirm(t('patientReports.texts_delete_mine_confirm'))) return;
              void run(() => onDeleteText(text.id));
            }}
          >
            {t('patientReports.texts_delete_mine')}
          </Button>
        ) : null}
        {canManage ? (
          <Button
            type="button"
            variant="tertiary"
            disabled={disabled || busy}
            onClick={() => {
              if (
                !window.confirm(t('patientReports.texts_delete_type_confirm', { name: type.name }))
              )
                return;
              void run(() => onDeleteType(type.id));
            }}
          >
            {t('patientReports.texts_delete_type')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
